import React from 'react'
import { StyleSheet, ScrollView, View } from 'react-native'
import { Text, Spinner, Button, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { saleSettingsAtom, constantsAtom, userStoreIdAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { saveSaleSettings } from '../../global/Firebase/firebase'
import { useContext, useState, useEffect } from 'react'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { useForm } from 'react-hook-form'
import FormTextInput from '../../components/FormTextInput'
import FormToggle from '../../components/FormToggle'
import FormSelect from '../../components/FormSelect'
import FormRadio from '../../components/FormRadio'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

const getFormValues = (saleSettings, selectedStoreId) => {
  const storeSettings = saleSettings[selectedStoreId] || {
    deliveryPrice: 0,
    deliveryPaidByCustomer: true,
    deliveryIncludeInReceipt: true,
    deliveryPaymentSourceWallet: null,
    defaultDeliverySpendingCategory: null,
    showSaveCartForLater: true,
    allowMultipleProductSelection: true,
    dateFieldVisible: false,
    storeFieldVisible: false,
    tableNumberFieldVisible: false,
    commentFieldVisible: false,
    showTotalPurchasePriceInCart: false,
    showPurchasePriceInEditCartItem: false,
    mergeSimilarItems: true
  }

  return {
    deliveryPrice: storeSettings.deliveryPrice?.toString() || '0',
    deliveryPaidByCustomer: storeSettings.deliveryPaidByCustomer,
    deliveryIncludeInReceipt: storeSettings.deliveryIncludeInReceipt,
    deliveryPaymentSourceWallet: storeSettings.deliveryPaymentSourceWallet,
    defaultDeliverySpendingCategory: storeSettings.defaultDeliverySpendingCategory,
    showSaveCartForLater: storeSettings.showSaveCartForLater,
    allowMultipleProductSelection: storeSettings.allowMultipleProductSelection,
    dateFieldVisible: storeSettings.dateFieldVisible,
    storeFieldVisible: storeSettings.storeFieldVisible,
    tableNumberFieldVisible: storeSettings.tableNumberFieldVisible,
    commentFieldVisible: storeSettings.commentFieldVisible,
    showTotalPurchasePriceInCart: storeSettings.showTotalPurchasePriceInCart,
    showPurchasePriceInEditCartItem: storeSettings.showPurchasePriceInEditCartItem,
    mergeSimilarItems: storeSettings.mergeSimilarItems
  }
}

export default function SaleSettingsScreen({ navigation }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const saleSettings = useAtomValue(saleSettingsAtom)
  const constants = useAtomValue(constantsAtom)
  const userStoreId = useAtomValue(userStoreIdAtom)
  const [context] = useContext(UserContext)
  const [selectedStoreId, setSelectedStoreId] = useState(userStoreId)
  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)

  const { setValue, handleSubmit, control } = useForm({
    defaultValues: getFormValues(saleSettings, selectedStoreId)
  })

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <HeaderIconButton
          onPress={() => navigation.goBack()}
          iconName='arrow-back-outline'
        />
      ),
      title: t('settings.saleSettings')
    })
  }, [navigation, t])

  // Update form values when store selection or settings change
  useEffect(() => {
    const formValues = getFormValues(saleSettings, selectedStoreId)
    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key, value)
    })
    // Note: Don't clear success message here - it gets cleared when user switches stores
    setSaveError('')
  }, [selectedStoreId, saleSettings, setValue])

  // Emit sale settings data for E2E testing
  useEffect(() => {
    if (selectedStoreId && saleSettings) {
      const currentSettings = saleSettings[selectedStoreId] || {}
      console.log('[SALE_SETTINGS_DATA]', JSON.stringify(sanitizeForFirestore({
        storeId: selectedStoreId,
        ...currentSettings,
      })))
    }
  }, [selectedStoreId, saleSettings])

  // Clear success message only when user switches stores
  useEffect(() => {
    setSuccessMessageVisible(false)
  }, [selectedStoreId])

  const onSubmit = async (data) => {
    setIsLoading(true)
    setSaveError('')
    
    try {
      const settingsToSave = {
        deliveryPrice: parseFloat(data.deliveryPrice) || 0,
        deliveryPaidByCustomer: data.deliveryPaidByCustomer,
        deliveryIncludeInReceipt: data.deliveryIncludeInReceipt,
        deliveryPaymentSourceWallet: data.deliveryPaymentSourceWallet,
        defaultDeliverySpendingCategory: data.defaultDeliverySpendingCategory,
        showSaveCartForLater: data.showSaveCartForLater,
        allowMultipleProductSelection: data.allowMultipleProductSelection,
        dateFieldVisible: data.dateFieldVisible,
        storeFieldVisible: data.storeFieldVisible,
        tableNumberFieldVisible: data.tableNumberFieldVisible,
        commentFieldVisible: data.commentFieldVisible,
        showTotalPurchasePriceInCart: data.showTotalPurchasePriceInCart,
        showPurchasePriceInEditCartItem: data.showPurchasePriceInEditCartItem,
        mergeSimilarItems: data.mergeSimilarItems
      }

      await saveSaleSettings(selectedStoreId, settingsToSave)
      setIsLoading(false)
      setSuccessMessageVisible(true)
    } catch (error) {
      setIsLoading(false)
      setSaveError(error.message)
      console.error('Error updating store sale settings:', error)
    }
  }

  const handleFormErrors = (errors) => {
    console.log('Form errors:', errors)
    setSaveError(t('notAllFieldsFilled'))
  }

  return (
    <ScrollView style={[styles.container, {backgroundColor: theme['background-basic-color-2']}]} contentContainerStyle={styles.contentContainer}>
      <StoreChooserHorizontal 
        store={selectedStoreId} 
        onChange={setSelectedStoreId}
        style={styles.storeChooser}
        showAll={false}
      />
      
      <Text category='h5' style={styles.title}>
        {t('newSale.deliveryDefaultSettings')}
      </Text>
      
      <FormRadio
        control={control}
        name="deliveryPaidByCustomer"
        options={[
          { value: true, label: t('newSale.deliveryPaidByCustomer') },
          { value: false, label: t('newSale.deliveryPaidBySeller') }
        ]}
        radioStyle={styles.radio}
        style={styles.field}
        label={t('newSale.defaultDeliveryPaidBy')}
      />
      
      <FormSelect
        control={control}
        name="deliveryPaymentSourceWallet"
        label={t('newSale.deliveryPaymentSourceWallet')}
        data={constants?.wallets?.data || []}
        style={styles.field}
      />
      
      <FormTextInput
        control={control}
        name="deliveryPrice"
        label={t('newSale.deliveryPrice')}
        placeholder="0"
        style={styles.field}
        isNumberInput={true}
        keyboardType='decimal-pad'
      />
      
      <FormToggle
        control={control}
        name="deliveryIncludeInReceipt"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.deliveryIncludeInReceipt')}
      </FormToggle>
      
      <FormSelect
        control={control}
        name="defaultDeliverySpendingCategory"
        label={t('newSale.defaultDeliverySpendingCategory')}
        data={constants?.spendingCategories?.data || []}
        style={styles.field}
      />

      <Text category='h5' style={styles.title}>
        {t('newSale.other')}
      </Text>
      
      <FormToggle
        control={control}
        name="showSaveCartForLater"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showSaveCartForLater')}
      </FormToggle>

      <Text category='h5' style={styles.title}>
        {t('newSale.interfaceSettings')}
      </Text>
      
      <FormToggle
        control={control}
        name="allowMultipleProductSelection"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.multipleProductSelection')}
      </FormToggle>

      <Text category='h5' style={styles.title}>
        {t('newSale.additionalFields')}
      </Text>
      
      <FormToggle
        control={control}
        name="dateFieldVisible"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showDateField')}
      </FormToggle>
      
      <FormToggle
        control={control}
        name="storeFieldVisible"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showStoreField')}
      </FormToggle>
      
      <FormToggle
        control={control}
        name="tableNumberFieldVisible"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showTableNumberField')}
      </FormToggle>
      
      <FormToggle
        control={control}
        name="commentFieldVisible"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showCommentField')}
      </FormToggle>
      
      <FormToggle
        control={control}
        name="showTotalPurchasePriceInCart"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showTotalPurchasePriceInCart')}
      </FormToggle>
      
      <FormToggle
        control={control}
        name="showPurchasePriceInEditCartItem"
        style={styles.toggleField}
        textStyle={{flexShrink: 1}}
      >
        {t('newSale.showPurchasePriceInEditCartItem')}
      </FormToggle>

      <FormRadio
        control={control}
        name="mergeSimilarItems"
        options={[
          { value: true, label: t('newSale.mergeSimilarItemsIntoOneRowTrue') },
          { value: false, label: t('newSale.mergeSimilarItemsIntoOneRowFalse') }
        ]}
        radioStyle={styles.radio}
        style={styles.field}
        label={t('newSale.mergeSimilarItemsIntoOneRow')}
      />

      {successMessageVisible && (
        <>
          <Text status='success' style={styles.messageText}>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
        </>
      )}

      {saveError !== '' && (
        <Text style={styles.messageText} status='danger'>{saveError}</Text>
      )}

      {isLoading ? (
        <View style={styles.spinner}><Spinner /></View>
      ) : (
        !successMessageVisible && (
          <Button style={styles.submitButton} onPress={handleSubmit(onSubmit, handleFormErrors)}>
            {t('save')}
          </Button>
        )
      )}

      <View style={{height: 50}} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  title: {
    marginTop: 15,
    marginBottom: 5
  },
  field: {
    marginBottom: 15
  },
  toggleField: {
    marginBottom: 5
  },
  radio: {
    marginBottom: 5,
  },
  storeChooser: {
    marginBottom: 20,
  },
  submitButton: {
    marginTop: 15
  },
  messageText: {
    marginVertical: 8,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
})
